import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';
import { ToggleOptionComponent } from 'app/shared/components/form-ui/toggle-option/toggle-option.component';
import { Observable, map, startWith, take } from 'rxjs';

import { ActionEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/action-entity.enum';
import { EntityEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/entity.enum';
import EntityFieldsCatalogAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/field-catalog.entity';
import { UserFieldEntityTypeEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/user-field-entity-type.enum';
import UserFieldCatalogAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/user-field-catalog/user-field-catalog.entity';
import { UserFieldConfigAttributes } from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/user-field-catalog/user-field-config/user-field-config.entity';
import { IAPIItemResult } from 'app/common/interfaces';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { SelectSchemaComponent } from 'app/shared/components/select-schema/select-schema.component';
import { ColDef, GridConfig, SmartGridComponent } from 'app/shared/components/smart-grid';
import { fieldTypeOptionsForm, ICON_OPTIONS, userEntityLabels } from 'app/shared/constants/lists.constant';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { FieldActionsCellComponent } from '../user-fields-catalog/modals-config/user-sub-fields-config/field-actions.component';
import { ValuesModalComponent } from '../user-fields-catalog/values-dialog/values-dialog.component';
import { CompanyFieldCatalogService } from './company-form-config.service';
import { AttachmentConfigDialogComponent } from './modals-config/attachment-config-dialog.component';
import { ConfirmDialogComponent } from './modals-config/confirm-dialog.component';
import { FieldGroupsDialogComponent } from '../field-groups-dialog/field-groups-dialog.component';
import { FieldGroup } from '../field-groups-dialog/field-group.interface';
import {
  fillNextFreePosition,
  normalizeFieldPositions,
  normalizeGroupPositions,
  normalizeIconColor,
} from '../field-groups-dialog/field-groups.util';
import { SubfieldsConfigComponent } from './modals-config/subfieldsconfig/subfieldsconfig.component';

@Component({
  selector: 'app-company-form-config',
  templateUrl: './company-form-config.component.html',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTabsModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatMenuModule,
    ToggleOptionComponent,
    FormSectionComponent,
    NgxMaskDirective,
    MatTooltipModule,
    MatProgressSpinnerModule,
    SmartGridComponent,
    SelectSchemaComponent,
  ],
  providers: [provideNgxMask()],
})
export class CompanyFormConfigComponent implements OnInit {
  viewMode: 'list' | 'edit' = 'list';
  editingFieldIndex: number | null = null;
  loading = true;
  gridData: any[] = [];
  private isNewField = false;

  entities: EntityEnum[] = Object.values(EntityEnum);
  selectedEntityIndex = 0;
  selectedAction: ActionEnum = ActionEnum.CREATE;

  formConfigs: Record<string, Record<string, FormGroup>> = {} as any;

  readonly entityLabels: Record<string, string> = {
    [EntityEnum.BUSINESS_PARTNER]: 'Nuevos Prospectos',
    [EntityEnum.CREDIT_APPLICATION]: 'Solicitudes de Crédito',
    [EntityEnum.CREATED_BUSINESS_PARTNER]: 'Socios de negocios',
  };

  /** Leyenda en catálogo de edición de socios de negocio (solo campos listados son editables en app). */
  get showBusinessPartnerEditFieldsLegend(): boolean {
    return (
      this.entities[this.selectedEntityIndex] === EntityEnum.CREATED_BUSINESS_PARTNER &&
      this.selectedAction === ActionEnum.UPDATE
    );
  }

  filteredEntities$: Observable<EntityEnum[]>;

  readonly fieldTypeOptions = fieldTypeOptionsForm;

  @ViewChild('addModal') addModal!: TemplateRef<any>;
  private dialogRefAdd: MatDialogRef<any>;

  columnDefs: ColDef<any>[] = [];

  // Stable field, NOT a getter — a getter hands SmartGrid a fresh config every change-detection
  // cycle, churning the grid.
  gridConfig: GridConfig<any> = {
    emptyState: {
      message: 'No hay campos. Haz clic en "Nuevo" para empezar.',
      showIcon: true,
    },
    pagination: { enabled: true, pageSize: 10, showPaginator: true },
    styling: { density: 'comfortable', fontSize: '12px' },
  };

  readonly userEntityList = userEntityLabels;
  userFieldsConfig: UserFieldConfigAttributes[] = [];

  private readonly autoSavingApplyOnly = new Set<string>();
  private readonly savingCatalog = new Set<string>();

  searchControl = new FormControl('');
  iconSearchControl = new FormControl('');
  filteredIcons$!: Observable<string[]>;

  readonly availableIcons = ICON_OPTIONS;

  isMobileMenuOpen = false;

  constructor(
    private fb: FormBuilder,
    private companyFieldCatalogService: CompanyFieldCatalogService,
    private handlerMessage: HandlerMessageService,
    public dialogRef: MatDialogRef<CompanyFormConfigComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: { companyId: string }
  ) {}

  ngOnInit(): void {
    this.buildColumnDefs();
    this.entities.forEach((entity) => {
      this.formConfigs[entity] = {
        [ActionEnum.CREATE]: this.createBaseForm(entity, ActionEnum.CREATE),
        [ActionEnum.UPDATE]: this.createBaseForm(entity, ActionEnum.UPDATE),
      };
    });

    this.setupSearch();
    this.setupIconSearch();
    this.entities.forEach((entity) => {
      [ActionEnum.CREATE, ActionEnum.UPDATE].forEach((action) => {
        this.setupApplyOnlyAppAutoSave(entity, action);
      });
    });
    this.loadAllConfigs();
  }

  private buildColumnDefs(): void {
    this.columnDefs = [
      { field: 'name', headerName: 'Nombre DB', sortable: true, width: 150, pinned: 'left', lockPinned: true },
      { field: 'label', headerName: 'Etiqueta', sortable: true, width: 200 },
      { field: 'group', headerName: 'Grupo', sortable: true, width: 200 },
      { field: 'position', headerName: 'Posición', sortable: true, width: 100 },
      { field: 'type', headerName: 'Tipo', sortable: true, width: 120 },
      {
        field: 'actions',
        headerName: 'Acciones',
        width: 100,
        pinned: 'right',
        lockPinned: true,
        cellRenderer: FieldActionsCellComponent,
        cellRendererParams: {
          onClick: (params: any) => this.editField(params.data.index),
          onDelete: (params: any) => this.deleteField(params.data.index),
        },
      },
    ];
  }

  private setupSearch() {
    this.filteredEntities$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || ''))
    );
  }

  private setupIconSearch() {
    this.filteredIcons$ = this.iconSearchControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const search = (value || '').toLowerCase();
        return this.availableIcons.filter((ic) => ic.toLowerCase().includes(search));
      })
    );
  }

  private _filter(value: string): EntityEnum[] {
    const filterValue = value.toLowerCase();
    return this.entities.filter((entity) => {
      const label = this.getEntityLabel(entity).toLowerCase();
      return label.includes(filterValue);
    });
  }

  onEntitySelect(entity: EntityEnum) {
    this.selectedEntityIndex = this.entities.indexOf(entity);
    this.updateGridData();
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  loadAllConfigs() {
    this.loading = true;

    this.companyFieldCatalogService
      .getFields(this.data.companyId, '_all', [ActionEnum.CREATE, ActionEnum.UPDATE])
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          const indexed = this.companyFieldCatalogService.indexEntityFieldsConfigs(res.data);
          this.entities.forEach((entity) => {
            [ActionEnum.CREATE, ActionEnum.UPDATE].forEach((action) => {
              const config = indexed.get(`${entity}:${action}`);
              const fieldsArray = this.getFieldsArray(entity, action);
              fieldsArray.clear();
              if (config) {
                this.formConfigs[entity][action].patchValue(
                  {
                    _id: this.resolveCatalogId(config),
                    groups: this.normalizeGroups((config as any).groups),
                    applyOnlyApp: config.applyOnlyApp ?? false,
                  },
                  { emitEvent: false }
                );
                if (config.fields?.length) {
                  config.fields.forEach((f) => fieldsArray.push(this.createFieldGroup(f)));
                  normalizeFieldPositions(fieldsArray);
                }
              }
            });
          });
          this.updateGridData();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private setupApplyOnlyAppAutoSave(entity: EntityEnum, action: ActionEnum): void {
    const applyOnlyAppControl = this.formConfigs[entity]?.[action]?.get('applyOnlyApp');
    applyOnlyAppControl?.valueChanges.subscribe(() => this.saveApplyOnlyAppConfig(entity, action));
  }

  private catalogKey(entity: EntityEnum, action: ActionEnum): string {
    return `${entity}:${action}`;
  }

  private resolveCatalogId(config: EntityFieldsCatalogAttributes & { id?: unknown }): string | null {
    const raw = config._id ?? config.id;
    if (raw == null || raw === '') return null;
    return String(raw);
  }

  private resolveFieldLabel(field: {
    label?: string;
    fieldLabel?: string;
  }): string {
    if (typeof field.label === 'string') return field.label;
    if (typeof field.fieldLabel === 'string') return field.fieldLabel;
    return '';
  }

  get isSavingCatalogForSelectedEntity(): boolean {
    const entity = this.entities[this.selectedEntityIndex];
    return this.savingCatalog.has(this.catalogKey(entity, this.selectedAction));
  }

  loadAllUsersFields(userFieldEntity: UserFieldEntityTypeEnum) {
    this.companyFieldCatalogService
      .getFieldsMaster(this.data.companyId, userFieldEntity)
      .pipe(take(1))
      .subscribe({
        next: (res: IAPIItemResult<UserFieldCatalogAttributes>) => {
          this.userFieldsConfig = res.data?.fields || [];
        },
      });
  }

  syncField(field: FormGroup) {
    const selectedFieldName = field.get('userField')?.value;
    const userField = this.userFieldsConfig.find((uf) => uf.fieldName === selectedFieldName);

    if (userField) {
      field.patchValue({
        name: userField.fieldName || '',
        label: userField.fieldLabel?.master || '',
        type: userField.fieldType || 'text',
        visible: userField.isVisible !== false,
        isTypeMandatory: userField.isTypeMandatory === true,
        required: userField.isRequired === true,
        group: userField.group || null,
        groupPosition: userField.groupPosition || 0,
        icon: userField.icon || null,
        iconColor: userField.iconColor || null,
        position: userField.position || null,
        maxLength: userField.maxLength ?? null,
        minLength: userField.minLength ?? null,
        caseConversion: userField.caseConversion ?? 'NONE',
        trimSpaces: !!userField.trimSpaces,
        mask: userField.mask ?? '',
        defaultValue: userField.defaultValue ?? (userField.fieldType === 'multiselect' ? [] : ''),
        valuesData: userField.valuesData || [],
        parentFieldName: userField.parentFieldName || '',
        schemaMaster: userField.schemaMaster || '',
        attachments: userField.attachments || [],
        multiple: userField.multiple,
      });
      field.get('name')?.updateValueAndValidity();
      this.handlerMessage.success({ message: 'Datos sincronizados desde el campo de usuario' });
    }
  }

  private createBaseForm(entity: string, action: ActionEnum): FormGroup {
    return this.fb.group({
      _id: [null],
      entity: [entity],
      action: [action],
      fields: this.fb.array([]),
      groups: [[] as FieldGroup[]],
      applyOnlyApp: [false],
    });
  }

  createFieldGroup(field): FormGroup {
    const group = this.fb.group({
      _id: [field._id || null],
      name: [
        { value: field.name || field.fieldName || '', disabled: !!(field.name || field.fieldName) },
        [Validators.required, this.uniqueNameValidator.bind(this)],
      ],
      label: [this.resolveFieldLabel(field), Validators.required],
      type: [
        {
          value: field.type || field.fieldType || 'text',
          disabled: !!(field.type || field.fieldType),
        },
      ],
      visible: [field.visible ?? field.isVisible ?? true],
      required: [!!(field.required ?? field.isRequired)],
      isTypeMandatory: [{ value: !!field.isTypeMandatory, disabled: field.isTypeMandatory }],
      position: [field.position ?? null, [Validators.required, Validators.min(1)]],
      groupId: [field.groupId ?? null],
      group: [field.group ?? ''],
      groupPosition: [field.groupPosition ?? 0],
      icon: [field.icon ?? ''],
      iconColor: [field.iconColor ?? ''],
      maxLength: [field.maxLength ?? null],
      minLength: [field.minLength ?? null, [this.minLengthRangeValidator.bind(this)]],
      caseConversion: [field.caseConversion ?? 'NONE'],
      trimSpaces: [!!field.trimSpaces],
      readOnly: [field.readOnly ?? false],
      mask: [field.mask ?? ''],
      defaultValue: [field.defaultValue ?? (field.type === 'multiselect' ? [] : '')],
      allowedExtensions: [field.allowedExtensions ?? null],
      maxFileSize: [field.maxFileSize ?? 100],
      multiple: [!!field.multiple],
      subFields: [field.subFields ?? []],
      valuesData: [field.valuesData ?? []],
      parentFieldName: [field.parentFieldName ?? ''],
      schemaMaster: [{ value: field.schemaMaster ?? '', disabled: !!field.schemaMaster }],
      attachments: [field.attachments ?? []],
      isUserField: [{ value: false, disabled: !!(field.name || field.fieldName) }],
      userFieldEntity: [],
      userField: [null],
    });

    ['groupId', 'position'].forEach((controlName) => {
      group.get(controlName)?.valueChanges.subscribe(() => this.revalidateGroupFields(group));
    });

    group.get('maxLength')?.valueChanges.subscribe(() => {
      group.get('minLength')?.updateValueAndValidity({ emitEvent: false });
    });

    return group;
  }

  addNewField() {
    const entity = this.entities[this.selectedEntityIndex];
    const fieldsArray = this.getFieldsArray(entity, this.selectedAction);
    this.isNewField = true;
    const newField = this.createFieldGroup({});
    fieldsArray.push(newField);
    fillNextFreePosition(newField, null);
    this.updateGridData();
    this.editField(fieldsArray.length - 1);
    this.formConfigs[this.entities[this.selectedEntityIndex]][
      this.selectedAction
    ].markAllAsTouched();
  }

  editField(index: number) {
    this.editingFieldIndex = index;
    const entity = this.entities[this.selectedEntityIndex];
    const action = this.selectedAction;
    const field = this.getFieldControls(entity, action)[index];
    field.markAllAsTouched();

    const revertSnapshot = !this.isNewField ? field.getRawValue() : null;

    this.dialogRefAdd = this.dialog.open(this.addModal, {
      width: '95vw',
      maxWidth: '800px',
      height: 'auto',
      maxHeight: '90vh',
      disableClose: true,
      panelClass: ['custom-dialog-container', 'responsive-modal'],
    });

    this.dialogRefAdd.afterClosed().subscribe((result) => {
      if (!result && this.isNewField) {
        this.deleteFieldSilently(this.editingFieldIndex);
      } else if (!result && !this.isNewField && revertSnapshot !== null) {
        field.patchValue(revertSnapshot, { emitEvent: false });
        field.markAsPristine();
        field.markAsUntouched();
        this.revalidateGroupFields(field);
        this.updateGridData();
      }
      this.isNewField = false;
    });
  }

  confirmFieldEdit() {
    const entity = this.entities[this.selectedEntityIndex];
    const action = this.selectedAction;
    const field = this.getFieldControls(entity, action)[this.editingFieldIndex];

    if (!field?.valid) {
      field?.markAllAsTouched();
      const details = this.collectInvalidFields(field ? [field] : []);
      this.handlerMessage.error({
        message: details.length
          ? `No se pudo guardar. Revisa: ${details.join(' | ')}`
          : 'Existen errores en el formulario',
      });
      return;
    }

    const fieldsArray = this.getFieldsArray(entity, action);
    const occupants = this.findPositionConflicts(field, fieldsArray);

    if (!occupants.length) {
      this.saveEditedField(entity, action);
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, {
        width: '460px',
        data: {
          title: 'Posición ocupada',
          message: this.buildPositionShiftMessage(occupants, Number(field.get('position')?.value)),
          confirmText: 'Recorrer y guardar',
          confirmColor: 'primary',
        },
        panelClass: 'responsive-modal',
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.shiftPositionsFrom(field, fieldsArray);
        this.revalidateGroupFields(field);
        this.saveEditedField(entity, action);
      });
  }

  private saveEditedField(entity: EntityEnum, action: ActionEnum): void {
    this.persistCatalog(entity, action, {
      successMessage: 'Campo guardado con éxito',
      onSuccess: () => {
        this.dialogRefAdd.close(true);
        this.updateGridData();
      },
    });
  }

  closeEditModal() {
    this.dialogRefAdd.close(false);
  }

  deleteField(index: number) {
    const entity = this.entities[this.selectedEntityIndex];
    const action = this.selectedAction;
    const fieldsArray = this.getFieldsArray(entity, action);
    const fieldGroup = fieldsArray.at(index) as FormGroup;
    const fieldLabel =
      fieldGroup.get('label')?.value || fieldGroup.get('name')?.value || 'este campo';

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmar eliminación',
        message: `¿Estás seguro de eliminar "${fieldLabel}"?`,
      },
      panelClass: 'responsive-modal',
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;

      if (this.dialogRefAdd) {
        this.dialogRefAdd.close(true);
      }
      fieldsArray.removeAt(index);
      this.updateGridData();
      this.editingFieldIndex = null;

      fieldsArray.controls.forEach((control) => {
        control.get('name')?.updateValueAndValidity({ emitEvent: false });
      });

      this.persistCatalog(entity, action, {
        successMessage: 'Campo eliminado',
        resyncOnError: true,
      });
    });
  }

  private deleteFieldSilently(index: number) {
    const entity = this.entities[this.selectedEntityIndex];
    const fieldsArray = this.getFieldsArray(entity, this.selectedAction);
    if (index !== null) {
      fieldsArray.removeAt(index);
      this.updateGridData();
    }
  }

  updateGridData() {
    const entity = this.entities[this.selectedEntityIndex];
    const controls = this.getFieldControls(entity, this.selectedAction);
    this.gridData = controls.map((fg, index) => ({
      ...fg.getRawValue(),
      group: this.groupName(fg.get('groupId')?.value),
      index,
    }));
  }

  getFieldsArray(entity: string, action: ActionEnum): FormArray {
    return this.formConfigs[entity][action].get('fields') as FormArray;
  }

  getFieldControls(entity: string, action: ActionEnum): FormGroup[] {
    return this.getFieldsArray(entity, action).controls as FormGroup[];
  }

  onTabChange(index: number) {
    this.selectedEntityIndex = index;
    this.updateGridData();
  }

  onActionChange(index: number) {
    this.selectedAction = index === 0 ? ActionEnum.CREATE : ActionEnum.UPDATE;
    this.updateGridData();
  }

  backToList() {
    this.viewMode = 'list';
    this.editingFieldIndex = null;
    this.updateGridData();
  }

  private buildPersistPayload(
    entity: EntityEnum,
    action: ActionEnum,
    rawValue: { applyOnlyApp?: boolean; groups?: FieldGroup[]; fields: Record<string, unknown>[] }
  ) {
    return {
      companyId: this.data.companyId,
      entity,
      action,
      applyOnlyApp: rawValue.applyOnlyApp ?? false,
      groups: rawValue.groups ?? [],
      fields: rawValue.fields.map((f) => ({
        fieldName: f['name'],
        fieldLabel: f['label'],
        fieldType: f['type'],
        isVisible: f['visible'],
        isRequired: f['required'],
        isTypeMandatory: f['isTypeMandatory'],
        position: Number(f['position'] || 0),
        groupId: f['groupId'] || null,
        maxLength: f['maxLength'] ? Number(f['maxLength']) : null,
        minLength: f['minLength'] ? Number(f['minLength']) : null,
        caseConversion: f['caseConversion'] ?? 'NONE',
        trimSpaces: !!f['trimSpaces'],
        readOnly: !!f['readOnly'],
        mask: f['mask'] ?? '',
        defaultValue: f['defaultValue'],
        allowedExtensions: f['allowedExtensions'],
        maxFileSize: f['maxFileSize'] ? Number(f['maxFileSize']) : null,
        multiple: f['multiple'],
        subFields: f['subFields'],
        valuesData: f['valuesData'],
        parentFieldName: f['parentFieldName'] ?? null,
        schemaMaster: f['schemaMaster'] ?? null,
        attachments: f['attachments'] ?? null,
      })),
    };
  }

  private persistCatalog(
    entity: EntityEnum,
    action: ActionEnum,
    options?: {
      successMessage?: string;
      onSuccess?: () => void;
      resyncOnError?: boolean;
    }
  ): void {
    const key = this.catalogKey(entity, action);
    if (this.savingCatalog.has(key)) return;

    const form = this.formConfigs[entity][action];
    const fieldsArray = this.getFieldsArray(entity, action);
    if (fieldsArray.invalid) {
      fieldsArray.markAllAsTouched();
      const details = this.collectInvalidFields(fieldsArray.controls);
      this.handlerMessage.error({
        message: details.length
          ? `No se guardó la configuración porque ${details.length} campo(s) del catálogo tienen datos inválidos: ${details.join(' | ')}. Corrígelos o reporta este detalle a soporte.`
          : 'Existen errores en los campos',
      });
      return;
    }

    const rawValue = form.getRawValue();
    const { _id: _catalogId, ...rest } = rawValue;
    const payload = this.buildPersistPayload(entity, action, rest);

    this.savingCatalog.add(key);
    this.companyFieldCatalogService.createField(payload).subscribe({
      next: (res: { data?: { _id?: string; id?: string } }) => {
        this.savingCatalog.delete(key);
        const savedId = res?.data?._id ?? res?.data?.id;
        if (savedId) {
          form.patchValue({ _id: String(savedId) }, { emitEvent: false });
        }
        this.handlerMessage.success({
          message: options?.successMessage ?? 'Configuración actualizada',
        });
        this.loadAllConfigs();
        options?.onSuccess?.();
      },
      error: (err) => {
        this.savingCatalog.delete(key);
        this.handlerMessage.error({
          message: err?.error?.message || err.message,
        });
        if (options?.resyncOnError) this.loadAllConfigs();
      },
    });
  }

  private saveApplyOnlyAppConfig(entity: EntityEnum, action: ActionEnum): void {
    const key = this.catalogKey(entity, action);
    if (this.autoSavingApplyOnly.has(key)) return;

    const form = this.formConfigs[entity][action];
    const rawValue = form.getRawValue();
    const applyOnlyApp = Boolean(rawValue.applyOnlyApp);
    const previousApplyOnlyApp = !applyOnlyApp;

    this.autoSavingApplyOnly.add(key);
    const payload = this.buildPersistPayload(entity, action, rawValue);

    this.companyFieldCatalogService.createField(payload).subscribe({
      next: (res: { data?: { _id?: string; id?: string } }) => {
        this.autoSavingApplyOnly.delete(key);
        const savedId = res?.data?._id ?? res?.data?.id;
        if (savedId) {
          form.patchValue({ _id: String(savedId) }, { emitEvent: false });
        }
        form.patchValue({ applyOnlyApp }, { emitEvent: false });
        this.handlerMessage.success({ message: 'Uso exclusivo de la APP actualizado' });
      },
      error: (err) => {
        this.autoSavingApplyOnly.delete(key);
        form.patchValue({ applyOnlyApp: previousApplyOnlyApp }, { emitEvent: false });
        this.handlerMessage.error({ message: err?.error?.message || err.message });
      },
    });
  }

  private normalizeGroups(groups: any[]): FieldGroup[] {
    const normalized: FieldGroup[] = (groups || []).map((group) => ({
      _id: group._id,
      name: group.name,
      position: group.position ?? 0,
      icon: group.icon ?? null,
      iconColor: normalizeIconColor(group.iconColor),
    }));
    return normalizeGroupPositions(normalized);
  }

  get currentGroups(): FieldGroup[] {
    const entity = this.entities[this.selectedEntityIndex];
    return this.formConfigs[entity]?.[this.selectedAction]?.get('groups')?.value ?? [];
  }

  groupName(groupId: string): string {
    return this.currentGroups.find((group) => group._id === groupId)?.name ?? 'Sin Grupo';
  }

  manageGroups(): void {
    const entity = this.entities[this.selectedEntityIndex];
    const action = this.selectedAction;
    const fieldsArray = this.getFieldsArray(entity, action);

    const fieldCountByGroupId: Record<string, number> = {};
    fieldsArray.controls.forEach((ctrl) => {
      const groupId = ctrl.get('groupId')?.value;
      if (groupId) fieldCountByGroupId[groupId] = (fieldCountByGroupId[groupId] ?? 0) + 1;
    });

    this.dialog
      .open(FieldGroupsDialogComponent, {
        width: '95vw',
        maxWidth: '1280px',
        disableClose: true,
        panelClass: ['custom-dialog-container', 'responsive-modal'],
        data: { groups: this.currentGroups, fieldCountByGroupId },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) return;

        this.formConfigs[entity][action].get('groups')?.setValue(result.groups);

        fieldsArray.controls.forEach((ctrl) => {
          if (result.removedGroupIds.includes(ctrl.get('groupId')?.value)) {
            ctrl.get('groupId')?.setValue(null, { emitEvent: false });
          }
        });

        this.persistCatalog(entity, action, {
          successMessage: 'Grupos actualizados',
          onSuccess: () => this.updateGridData(),
        });
      });
  }

  isConfigButtonDisabledBySchema(fieldGroup: FormGroup): boolean {
    const type = fieldGroup.get('type')?.value;
    const hasSchema = !!fieldGroup.get('schemaMaster')?.value;
    return ['select', 'multiselect'].includes(type) && hasSchema;
  }

  openValuesModal(fieldGroup: FormGroup) {
    const type = fieldGroup.get('type').value;
    const config = { width: '650px', data: {} };

    if (type === 'attachment') {
      config.data = {
        attachments: fieldGroup.get('attachments').value,
        multiple: fieldGroup.get('multiple').value,
        entity: this.entities[this.selectedEntityIndex],
      };
      this.dialog
        .open(AttachmentConfigDialogComponent, config)
        .afterClosed()
        .subscribe((res) => {
          if (res) fieldGroup.patchValue({ attachments: res.attachments, multiple: res.multiple });
        });
    } else if (type === 'form') {
      config.width = '80%';
      config.data = {
        subFields: fieldGroup.get('subFields').value,
        multiple: fieldGroup.get('multiple').value,
        entity: this.entities[this.selectedEntityIndex],
        companyId: this.data.companyId,
      };
      this.dialog
        .open(SubfieldsConfigComponent, config)
        .afterClosed()
        .subscribe((res) => {
          if (res) fieldGroup.patchValue({ subFields: res.subFields, multiple: res.multiple });
        });
    } else {
      config.width = '500px';
      config.data = { values: fieldGroup.get('valuesData').value };
      this.dialog
        .open(ValuesModalComponent, config)
        .afterClosed()
        .subscribe((res) => {
          if (res) fieldGroup.get('valuesData').setValue(res);
        });
    }
  }

  private controlLabels: Record<string, string> = {
    name: 'Nombre',
    label: 'Etiqueta',
    position: 'Posición',
    group: 'Grupo',
    groupPosition: 'Posición de grupo',
    minLength: 'Mín Char',
    maxLength: 'Máx Char',
    defaultValue: 'Valor por defecto',
    schemaMaster: 'Schema',
  };

  private describeErrors(errors: Record<string, unknown> | null): string {
    if (!errors) return '';
    return Object.keys(errors)
      .map((key) => {
        switch (key) {
          case 'required':
            return 'es obligatorio';
          case 'duplicateName':
            return 'nombre duplicado';
          case 'minGreaterThanMax':
            return 'la longitud mínima supera la máxima';
          default:
            return key;
        }
      })
      .join(', ');
  }

  private collectInvalidFields(fields: AbstractControl[]): string[] {
    const details: string[] = [];
    fields.forEach((ctrl, index) => {
      const fieldGroup = ctrl as FormGroup;
      if (fieldGroup.valid) return;
      const fieldName = this.describeFieldGroup(fieldGroup, index);
      const invalidControls = Object.keys(fieldGroup.controls)
        .filter((controlName) => fieldGroup.get(controlName)?.invalid)
        .map((controlName) => {
          const label = this.controlLabels[controlName] ?? controlName;
          const reason = this.describeErrors(fieldGroup.get(controlName)?.errors);
          return reason ? `${label} (${reason})` : label;
        });
      details.push(`${fieldName}: ${invalidControls.join(', ')}`);
      console.warn('[form-config] campo inválido', {
        index,
        fieldName,
        errors: Object.keys(fieldGroup.controls).reduce((acc, controlName) => {
          const control = fieldGroup.get(controlName);
          if (control?.invalid) acc[controlName] = control.errors;
          return acc;
        }, {} as Record<string, unknown>),
        rawValue: fieldGroup.getRawValue(),
      });
    });
    return details;
  }

  getEntityLabel(entity: string): string {
    return this.entityLabels[entity] ?? entity;
  }

  minLengthRangeValidator(control: AbstractControl) {
    const formGroup = control.parent as FormGroup;
    if (!formGroup) return null;
    const min = control.value;
    const max = formGroup.get('maxLength')?.value;
    if (min === null || min === '' || max === null || max === '') return null;
    return Number(min) > Number(max) ? { minGreaterThanMax: true } : null;
  }

  uniqueNameValidator(control: AbstractControl) {
    const formGroup = control.parent as FormGroup;
    if (!formGroup) return null;
    const value = control.value?.trim().toLowerCase();
    const formArray = formGroup.parent as FormArray;
    if (!formArray || !value) return null;
    const isDuplicate = formArray.controls.some(
      (ctrl) => ctrl !== formGroup && ctrl.get('name')?.value?.trim().toLowerCase() === value
    );
    return isDuplicate ? { duplicateName: true } : null;
  }

  private findPositionConflicts(fieldGroup: FormGroup, fieldsArray: FormArray): FormGroup[] {
    const currentGroup = fieldGroup.get('groupId')?.value ?? '';
    const currentPos = fieldGroup.get('position')?.value;
    if (currentPos === null || currentPos === '') return [];
    return fieldsArray.controls.filter(
      (ctrl) =>
        ctrl !== fieldGroup &&
        (ctrl.get('groupId')?.value ?? '') === currentGroup &&
        Number(ctrl.get('position')?.value) === Number(currentPos)
    ) as FormGroup[];
  }

  private shiftPositionsFrom(fieldGroup: FormGroup, fieldsArray: FormArray): void {
    const currentGroup = fieldGroup.get('groupId')?.value ?? '';
    const fromPosition = Number(fieldGroup.get('position')?.value);
    fieldsArray.controls.forEach((ctrl) => {
      if (ctrl === fieldGroup) return;
      if ((ctrl.get('groupId')?.value ?? '') !== currentGroup) return;
      const position = Number(ctrl.get('position')?.value) || 0;
      if (position < fromPosition) return;
      ctrl.get('position')?.setValue(position + 1, { emitEvent: false });
    });
  }

  getPositionOccupantLabel(fieldGroup: FormGroup): string | null {
    const entity = this.entities[this.selectedEntityIndex];
    const fieldsArray = this.getFieldsArray(entity, this.selectedAction);
    const occupants = this.findPositionConflicts(fieldGroup, fieldsArray);
    if (!occupants.length) return null;
    return occupants.map((ctrl) => this.describeFieldGroup(ctrl, 0)).join(', ');
  }

  private describeFieldGroup(fieldGroup: FormGroup, index: number): string {
    return fieldGroup.get('label')?.value || fieldGroup.get('name')?.value || `Campo #${index + 1}`;
  }

  private buildPositionShiftMessage(occupants: FormGroup[], position: number): string {
    const names = occupants.map((ctrl) => `"${this.describeFieldGroup(ctrl, 0)}"`).join(', ');
    return (
      `La posición ${position} ya está ocupada por ${names}.

` +
      'Si continúas, ese campo y los siguientes de su grupo se recorrerán una posición ' +
      'para dejar el orden solicitado.'
    );
  }

  onGroupSelected(fieldGroup: FormGroup, groupId: string | null): void {
    fillNextFreePosition(fieldGroup, groupId);
  }

  private revalidateGroupFields(currentFormGroup: FormGroup) {
    const formArray = currentFormGroup.parent as FormArray;
    if (!formArray) return;
    formArray.controls.forEach((control) => {
      const fg = control as FormGroup;
      fg.get('position')?.updateValueAndValidity({ emitEvent: false });
      fg.get('groupPosition')?.updateValueAndValidity({ emitEvent: false });
    });
    // Sin esto el arreglo queda pegado en INVALID aunque los campos ya se corrigieron
    formArray.updateValueAndValidity({ emitEvent: false });
  }
}
